import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type BadgeVariant = "default" | "primary" | "success" | "warning" | "error" | "info";

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>{
  children: ReactNode;
  variant?: BadgeVariant;
  className?: string;
  /** Show a status dot before the label */
  withDot?: boolean;
  /** Only meaningful with `onClick` — renders as a real disabled <button>, not just dimmed. */
  disabled?: boolean;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: "bg-secondary/10 text-secondary",
  primary: "bg-primary text-white",
  success: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  warning: "bg-amber-50 text-amber-800 border border-amber-100",
  error: "bg-red-50 text-red-700 border border-red-100",
  info: "bg-accent-50 text-accent-700 border border-accent-100",
};

const dotStyles: Record<BadgeVariant, string> = {
  default: "bg-secondary",
  primary: "bg-white",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  error: "bg-red-500",
  info: "bg-accent-500",
};

export function Badge({
  children,
  variant = "default",
  className = "",
  withDot = false,
  onClick,
  onKeyDown,
  ...props
}: BadgeProps) {
  const classes = cn(
    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
    onClick && "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
    variantStyles[variant],
    className,
  );
  const dot = withDot ? (
    <span className={cn("h-1.5 w-1.5 rounded-full", dotStyles[variant])} aria-hidden />
  ) : null;

  // A clickable badge is a button in practice (several callers use it as
  // one) — render a real <button> so an ancestor <fieldset disabled> (the
  // wizard's read-only mode) disables it natively, instead of a <span
  // role="button"> that fieldset can never reach.
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLButtonElement> | undefined}
        className={classes}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {dot}
        {children}
      </button>
    );
  }

  return (
    <span className={classes} onKeyDown={onKeyDown} {...props}>
      {dot}
      {children}
    </span>
  );
}
